import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { billingAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { 
  CreditCard, Check, Loader2, Gift, Users, ExternalLink, Copy, 
  CheckCircle, Clock, AlertTriangle, XCircle, Sparkles, Shield
} from 'lucide-react';
import { toast } from 'sonner';

// Capacity-based pricing - all plans have full feature access
const TIER_FEATURES = {
  starter: ['Up to 3 vehicles', '1 warehouse', 'Full feature access', 'Email support'],
  growth: ['Up to 10 vehicles', 'Up to 3 warehouses', 'Full feature access', 'Priority support'],
  pro: ['Unlimited vehicles', 'Unlimited warehouses', 'Full feature access', 'Dedicated support'],
};

export const Subscription = () => {
  const { isOwner } = useAuth();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState([]);
  const [status, setStatus] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    fetchData();
    
    // Check for success/cancel query params
    if (searchParams.get('success') === 'true') {
      toast.success('Subscription activated successfully!');
      // Clear query params
      window.history.replaceState({}, '', '/subscription');
    } else if (searchParams.get('canceled') === 'true') {
      toast.info('Checkout was canceled');
      window.history.replaceState({}, '', '/subscription');
    }
  }, [searchParams]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pricingRes, statusRes, referralRes] = await Promise.all([
        billingAPI.getPricing(),
        billingAPI.getStatus(),
        billingAPI.getReferralStats(),
      ]);
      setPricing(pricingRes.data);
      setStatus(statusRes.data);
      setReferralStats(referralRes.data);
    } catch (error) {
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (tierId) => {
    setCheckoutLoading(tierId);
    try {
      const response = await billingAPI.createCheckout(tierId, window.location.origin);
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setCheckoutLoading(null);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await billingAPI.createPortal(`${window.location.origin}/subscription`);
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portal_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
      setPortalLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (referralStats?.referral_code) {
      navigator.clipboard.writeText(referralStats.referral_code);
      toast.success('Referral code copied!');
    }
  };

  const getStatusBadge = (billingStatus) => {
    const styles = {
      trial: { icon: Clock, color: 'bg-blue-100 text-blue-700', label: 'Trial' },
      active: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Active' },
      past_due: { icon: AlertTriangle, color: 'bg-yellow-100 text-yellow-700', label: 'Past Due' },
      canceled: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Canceled' },
      incomplete: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-700', label: 'Incomplete' },
    };
    const config = styles[billingStatus] || styles.trial;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3">
          <CreditCard className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold font-heading">Subscription</h1>
            <p className="text-sm text-muted-foreground">Manage your billing and subscription</p>
          </div>
        </div>

        {/* Current Status Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Current Plan</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              {getStatusBadge(status?.billing_status)}
              {status?.plan_name && (
                <Badge variant="outline" className="text-base px-3 py-1">
                  {status.plan_name}
                </Badge>
              )}
              {/* Read-only beta/founder badge - hidden for comped tenants */}
              {!status?.is_comped && (status?.is_founder ? (
                <Badge className="bg-amber-100 text-amber-700 gap-1" data-testid="founder-badge">
                  <Sparkles className="w-3 h-3" />
                  Founder: $10 off/mo
                </Badge>
              ) : status?.is_beta && (
                <Badge className="bg-purple-100 text-purple-700 gap-1" data-testid="beta-badge">
                  <Sparkles className="w-3 h-3" />
                  Beta: $10 off/mo
                </Badge>
              ))}
              {status?.free_months_available > 0 && (
                <Badge className="bg-green-100 text-green-700 gap-1">
                  <Gift className="w-3 h-3" />
                  {status.free_months_available} free month{status.free_months_available > 1 ? 's' : ''} available
                </Badge>
              )}
            </div>
            
            {status?.current_period_end && (
              <p className="text-sm text-muted-foreground">
                Next billing date: {new Date(status.current_period_end).toLocaleDateString()}
              </p>
            )}

            {status?.billing_managed_in_stripe && (
              <Button
                variant="outline"
                onClick={handleManageSubscription}
                disabled={portalLoading}
                data-testid="manage-subscription-stripe-btn"
              >
                {portalLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Manage Subscription
              </Button>
            )}

            {status?.is_comped && (
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-blue-50 border border-blue-200 text-blue-800 text-sm" data-testid="comped-billing-notice">
                <Shield className="w-4 h-4 shrink-0" />
                <span>This plan is managed by an administrator. Billing is not handled through Stripe.</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing Tiers */}
        {!status?.has_subscription && (
          <>
            <div className="mt-8">
              <h2 className="text-xl font-bold">Choose a Plan</h2>
              <p className="text-sm text-muted-foreground mt-1">
                All plans include full feature access. Plans differ only by vehicle and warehouse capacity.
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              {pricing.map((tier) => {
                const features = TIER_FEATURES[tier.tier_id] || tier.features;
                const hasDiscount = !status?.is_comped && (status?.is_beta || status?.is_founder);
                const discountedPrice = hasDiscount 
                  ? tier.price_monthly - (status.beta_discount_amount || 1000)
                  : tier.price_monthly;
                
                return (
                  <Card 
                    key={tier.tier_id}
                    className={tier.tier_id === 'growth' ? 'border-primary shadow-lg' : ''}
                  >
                    <CardHeader>
                      {tier.tier_id === 'growth' && (
                        <Badge className="w-fit mb-2">Most Popular</Badge>
                      )}
                      <CardTitle>{tier.name}</CardTitle>
                      <div className="space-y-1">
                        <div className="flex items-baseline gap-2">
                          <span className="text-3xl font-bold">${discountedPrice / 100}</span>
                          <span className="text-muted-foreground">/month</span>
                        </div>
                        {hasDiscount && (
                          <p className={`text-sm ${status?.is_founder ? 'text-amber-600' : 'text-purple-600'}`}>
                            <span className="line-through text-muted-foreground mr-2">
                              ${tier.price_monthly / 100}
                            </span>
                            {status?.is_founder ? 'Founder' : 'Beta'} discount applied!
                          </p>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <ul className="space-y-2">
                        {features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500 shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button
                        className="w-full"
                        variant={tier.tier_id === 'growth' ? 'default' : 'outline'}
                        onClick={() => handleSubscribe(tier.tier_id)}
                        disabled={checkoutLoading !== null}
                      >
                        {checkoutLoading === tier.tier_id ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : null}
                        Subscribe
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* Referral Program */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              Referral Program
            </CardTitle>
            <CardDescription>
              Earn a free month for each friend who subscribes. Share your unique code!
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Referral Code */}
            <div className="flex items-center gap-2">
              <Input
                value={referralStats?.referral_code || ''}
                readOnly
                className="font-mono text-lg max-w-[200px]"
              />
              <Button variant="outline" size="icon" onClick={copyReferralCode}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4">
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{referralStats?.total_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Total Referrals</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{referralStats?.qualified_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Qualified</p>
              </div>
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{referralStats?.pending_referrals || 0}</p>
                <p className="text-xs text-muted-foreground">Pending</p>
              </div>
              <div className="text-center p-3 bg-green-100 rounded-lg">
                <p className="text-2xl font-bold text-green-700">
                  {referralStats?.free_months_available || 0}
                </p>
                <p className="text-xs text-green-600">Free Months</p>
              </div>
            </div>

            {/* Referral List */}
            {referralStats?.referrals?.length > 0 && (
              <div className="pt-4">
                <h4 className="font-semibold mb-2">Your Referrals</h4>
                <div className="space-y-2">
                  {referralStats.referrals.map((ref) => (
                    <div 
                      key={ref.id}
                      className="flex items-center justify-between p-2 bg-muted/50 rounded"
                    >
                      <div>
                        <p className="font-medium">
                          {ref.referred_tenant_name || ref.referred_email || 'Pending signup'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(ref.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={ref.status === 'qualified' ? 'default' : 'secondary'}>
                        {ref.status.replace('_', ' ')}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};
